/**
 * Helper utilities for the Intermarket Divergence algorithm
 * Synchronized with backend/algorithms/intermarket_divergence.py
 */

export const RAW_TICKER_ALIASES = {
    'S&P500': ['SP500', 'S&P500', 'SPX', '^GSPC', 'US500'],
    'SPX': ['SP500', 'S&P500', 'SPX', '^GSPC', 'US500'],
    'NASDAQ': ['NASDAQ', 'NDX', '^NDX', 'QQQ', 'NAS100', 'US100'],
    'NDX': ['NASDAQ', 'NDX', '^NDX', 'QQQ', 'NAS100', 'US100'],
    'USDX': ['DXY', 'USDX', 'USD', 'DX-Y.NYB', 'DXY0'],
    'DXY': ['DXY', 'USDX', 'USD', 'DX-Y.NYB', 'DXY0'],
    'WTI': ['OIL', 'CRUDE', 'WTI', 'USOIL', 'CL=F'],
    'USOIL': ['OIL', 'CRUDE', 'WTI', 'USOIL', 'CL=F'],
    'GOLD': ['GOLD', 'XAUUSD', 'XAU', 'GC=F'],
    'XAUUSD': ['GOLD', 'XAUUSD', 'XAU', 'GC=F'],
    'SILVER': ['SILVER', 'XAGUSD', 'XAG', 'SI=F'],
    'XAGUSD': ['SILVER', 'XAGUSD', 'XAG', 'SI=F'],
    'BTCUSD': ['BTC', 'BITCOIN', 'BTCUSD', 'BTC-USD'],
    'ETHUSD': ['ETH', 'ETHEREUM', 'ETHUSD', 'ETH-USD'],
    'EURUSD': ['EURUSD', 'EUR-USD', 'EUR/USD'],
    'GBPUSD': ['GBPUSD', 'GBP-USD', 'GBP/USD'],
    'USDJPY': ['USDJPY', 'USD-JPY', 'USD/JPY'],
};

export const MAX_EVENT_AGE_DAYS = {
    'D': 5,
    'W': 14,
    'M': 45,
    'MTD': 45,
    'Q': 100,
    'HY': 185,
    'Y': 365,
    'YTD': 365,
};

export const cleanTicker = (t) => {
    if (!t) return '';
    return String(t).toUpperCase().replace(/[/\\s\-^=._\\]/g, '');
};

export const getTickerAliases = (ticker) => {
    const cleaned = cleanTicker(ticker);
    if (!cleaned) return new Set();

    const aliases = new Set([cleaned]);
    for (const [key, aliasList] of Object.entries(RAW_TICKER_ALIASES)) {
        const cleanedKey = cleanTicker(key);
        const cleanedAliases = aliasList.map(cleanTicker);
        if (cleaned === cleanedKey || cleanedAliases.includes(cleaned)) {
            aliases.add(cleanedKey);
            cleanedAliases.forEach(a => aliases.add(a));
        }
    }
    return aliases;
};

export const parseEventDate = (dateVal) => {
    if (!dateVal) return null;
    const dt = new Date(dateVal);
    return isNaN(dt.getTime()) ? null : dt;
};

export const isTimeframeMatchingHorizon = (timeframe, horizonStr) => {
    if (!horizonStr) return true;
    const h = String(horizonStr).toLowerCase();
    const tf = String(timeframe).toUpperCase();
    if (h.includes('short')) {
        return ['D', 'W'].includes(tf);
    } else if (h.includes('medium') || h.includes('middle') || h.includes('mid')) {
        return ['W', 'M', 'MTD', 'Q'].includes(tf);
    } else if (h.includes('long')) {
        return ['M', 'Q', 'HY', 'Y', 'YTD'].includes(tf);
    }
    return true; // Fallback if no specific horizon keyword matches
};

export const isTimeframeMatchingDuration = isTimeframeMatchingHorizon;

export const getAdvancedEventSignal = (ticker, overviewRows, activePeriod = 'D', minScore = 2) => {
    if (!overviewRows || !Array.isArray(overviewRows)) return null;

    const aliases = getTickerAliases(ticker);
    if (aliases.size === 0) return null;

    const now = Date.now();
    const tf = (activePeriod || 'D').toUpperCase();
    const maxAgeDays = MAX_EVENT_AGE_DAYS[tf] || 30;
    const maxAgeMs = maxAgeDays * 86400 * 1000;

    let bullishScore = 0;
    let bearishScore = 0;
    const seenEvents = new Set();

    for (const event of overviewRows) {
        if (!event) continue;
        // Skip status / score summary rows
        if (event.status_short_term !== undefined || event.score_short_term !== undefined ||
            event.status_middle_term !== undefined || event.score_middle_term !== undefined ||
            event.status_long_term !== undefined || event.score_long_term !== undefined) {
            continue;
        }

        const assetsStr = event.Assets || event.assets || '';
        const directionStr = event.Direction || event.direction || '';
        if (!assetsStr || !directionStr) continue;

        // Event deduplication
        const eventId = event.id;
        const eventKey = eventId !== undefined ? eventId : `${event.Headline || event.headline}_${event.Date || event.date}_${assetsStr}_${directionStr}`;
        if (seenEvents.has(eventKey)) continue;
        seenEvents.add(eventKey);

        // Recency validation
        const eventDateVal = event.Date || event.date;
        const eventDt = parseEventDate(eventDateVal);
        if (eventDt) {
            const ageMs = now - eventDt.getTime();
            if (ageMs > maxAgeMs) continue;
        } else if (eventDateVal !== undefined && eventDateVal !== null && String(eventDateVal).trim() !== '') {
            continue;
        }

        const assetsArr = assetsStr.split(',').map(cleanTicker).filter(Boolean);
        const isMatch = assetsArr.some(a => aliases.has(a));

        const horizonStr = event.Horizon || event.horizon || event.Duration || event.duration;
        if (isMatch && isTimeframeMatchingHorizon(tf, horizonStr)) {
            let weight = 1;
            const impactStr = (event.Impact || event.impact || '').toLowerCase();
            if (impactStr.includes('high') || impactStr.includes('critical') || impactStr.includes('very')) {
                weight = 3;
            } else if (impactStr.includes('medium') || impactStr.includes('mod')) {
                weight = 2;
            }

            const dir = directionStr.toLowerCase();
            if (dir.includes('bull') || dir.includes('buy') || dir.includes('long') || dir === '1') {
                bullishScore += weight;
            } else if (dir.includes('bear') || dir.includes('sell') || dir.includes('short') || dir === '-1') {
                bearishScore += weight;
            }
        }
    }

    const netScore = bullishScore - bearishScore;
    if (netScore >= minScore) return 'BUY';
    if (netScore <= -minScore) return 'SELL';
    return null;
};

export const getThreshold = (timeframe) => {
    const tf = (timeframe || '').toUpperCase();
    if (tf === 'D') return 0.3;
    if (tf === 'W') return 0.8;
    if (tf === 'M' || tf === 'MTD') return 1.2;
    if (tf === 'Q') return 1.5;
    if (tf === 'HY' || tf === 'Y' || tf === 'YTD') return 2.0;
    return 1.5;
};

export const getDivergenceSignal = (eventSignal, perfPct, timeframe = 'D') => {
    const threshold = getThreshold(timeframe);

    if (!eventSignal) {
        return {
            divergence: 'NEUTRAL',
            category: 'NO_SIGNAL',
            label: 'No Macro Signal',
            color: '#78909c',
            perfPct,
            threshold,
            severity: 'NONE'
        };
    }

    if (eventSignal === 'BUY') {
        if (perfPct < -threshold) {
            const isExtreme = perfPct <= -3.0 * threshold;
            return {
                divergence: 'BULLISH_DIVERGENCE',
                category: 'DIVERGENCE',
                label: isExtreme ? 'Severe Bullish Divergence (Deep Value Dip)' : 'Bullish Divergence (Dip / Value Opportunity)',
                color: isExtreme ? '#26a69a' : '#4db6ac',
                bias: 'BUY',
                perfPct,
                threshold,
                severity: isExtreme ? 'EXTREME' : 'NORMAL'
            };
        } else if (perfPct > threshold) {
            return {
                divergence: 'CONFIRMATION_LONG',
                category: 'CONFIRMATION',
                label: 'Bullish Momentum Confirmed',
                color: '#80cbc4',
                bias: 'BUY',
                perfPct,
                threshold,
                severity: 'NORMAL'
            };
        }
    }

    if (eventSignal === 'SELL') {
        if (perfPct > threshold) {
            const isExtreme = perfPct >= 3.0 * threshold;
            return {
                divergence: 'BEARISH_DIVERGENCE',
                category: 'DIVERGENCE',
                label: isExtreme ? 'Severe Bearish Divergence (Exhaustion Rally Trap)' : 'Bearish Divergence (Rally / Overextension Trap)',
                color: isExtreme ? '#ef5350' : '#e57373',
                bias: 'SELL',
                perfPct,
                threshold,
                severity: isExtreme ? 'EXTREME' : 'NORMAL'
            };
        } else if (perfPct < -threshold) {
            return {
                divergence: 'CONFIRMATION_SHORT',
                category: 'CONFIRMATION',
                label: 'Bearish Momentum Confirmed',
                color: '#ef9a9a',
                bias: 'SELL',
                perfPct,
                threshold,
                severity: 'NORMAL'
            };
        }
    }

    return {
        divergence: 'NEUTRAL',
        category: 'IN_BALANCE',
        label: 'Market Within Noise Threshold',
        color: '#78909c',
        perfPct,
        threshold,
        severity: 'NONE'
    };
};
